import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all border-b ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-damita-border"
          : "bg-white/40 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link
          data-testid="brand-logo"
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="Bada Biplab Power Solutions"
            className="h-10 md:h-12 w-auto object-contain"
          />
          <span className="hidden xl:inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-damita-ink2 border-l border-damita-border pl-3 ml-1">
            BB Power
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-damita-blue" : "text-damita-ink hover:text-damita-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-call"
            href={`tel:${BRAND.phoneHref}`}
            className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm font-mono text-damita-ink2 hover:text-damita-blue"
          >
            <Phone size={14} />
            {BRAND.phone}
          </a>
          <button
            data-testid="cta-quote"
            onClick={() => navigate("/contact")}
            className="hidden md:inline-flex items-center gap-2 bg-damita-blue text-white px-4 py-2.5 text-sm font-semibold hover:bg-damita-blueHover transition-all btn-press"
          >
            Request a Quote
            <span aria-hidden>→</span>
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-damita-border bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-left py-3 border-b border-damita-border text-sm font-medium hover:text-damita-blue"
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${BRAND.phoneHref}`}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-damita-blue text-white py-3 font-semibold"
            >
              <Phone size={14} /> {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
